/**
 * Global "product guidance" (feature tours) preference.
 *
 * When guidance is paused, no tour auto-starts on any page - users can explore
 * freely. The preference persists per-user on the backend (stored as a reserved
 * key inside `tour_completions`) and is mirrored in localStorage as a fast cache
 * so we don't flash a tour before the profile has loaded.
 */

// Reserved key inside the tour_completions map. Its presence = guidance OFF.
// Never collides with a real tourId (those never start with "__").
export const GUIDANCE_DISABLED_KEY = '__guidance_disabled__';

const GUIDANCE_STORAGE_KEY = 'pons_guidance_disabled';

/** Fast localStorage read used before the Redux/profile state has loaded. */
export function isGuidancePausedLocal(): boolean {
  try {
    return localStorage.getItem(GUIDANCE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Mirror the preference to localStorage (best-effort). */
export function setGuidancePausedLocal(paused: boolean): void {
  try {
    if (paused) {
      localStorage.setItem(GUIDANCE_STORAGE_KEY, '1');
    } else {
      localStorage.removeItem(GUIDANCE_STORAGE_KEY);
    }
  } catch {
    // localStorage may be unavailable - ignore
  }
}

// ── Dev/testing only ────────────────────────────────────────────────────────
// When set, tours ignore their "completed" state and auto-start again, so a
// user who has already seen everything (like a local dev account) can still
// exercise the tours + the guidance toggle. Purely localStorage - no backend
// or real completion data is touched. Toggle it off (or clear the key) when done.
const TOUR_FORCE_KEY = 'pons_tour_force';

export function isTourForceLocal(): boolean {
  try {
    return localStorage.getItem(TOUR_FORCE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setTourForceLocal(force: boolean): void {
  try {
    if (force) {
      localStorage.setItem(TOUR_FORCE_KEY, '1');
    } else {
      localStorage.removeItem(TOUR_FORCE_KEY);
    }
  } catch {
    // ignore
  }
}
